<script lang="ts" setup>
import {
  mdiAccountMultiple as mdiComposers,
  mdiViewDashboard as mdiDashboard,
  mdiCalendar as mdiSeasons,
  mdiCog as mdiSettings,
  mdiFolderMusic as mdiSubmissions,
  mdiShieldLock as mdiTeam,
} from "@mdi/js";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { type RouteLocationRaw, useRoute } from "vue-router";
import { useDisplay } from "vuetify";

import { useAuthAccount } from "#composables/use-auth-account";
import { useSeasonStore } from "#modules/season/season.store";
import {
  usePlatformStatsComposersQuery,
  usePlatformStatsSeasonSubmissionsQuery,
} from "#modules/stats/stats.queries";
import { routeNames } from "#src/router/constants";
import { createGetElId } from "#utilities/element";

defineProps<{
  /** Whether to show drawer (affects mobile only) */
  showOnMobile: boolean;
}>();

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/prefer-function-type
  (e: "toggle-drawer"): void;
}>();

/** Get HTML element ID (with shared component prefix) */
const getElId = createGetElId("the-app-drawer");

interface DrawerNavItem {
  badge?: {
    color?: string;
    label?: string;
    value: string | number | undefined;
  };
  children?: DrawerNavItem[];
  disabled?: boolean;
  exact?: boolean;
  icon: string;
  to: RouteLocationRaw;
  title: string;
}

const composerStatsQuery = usePlatformStatsComposersQuery();
const pendingComposers = computed(() => composerStatsQuery.data.value?.pending);

const { currentSeasonId } = storeToRefs(useSeasonStore());
const submissionsStatsQuery = usePlatformStatsSeasonSubmissionsQuery(currentSeasonId);
const submissionsForReview = computed(() => submissionsStatsQuery.data.value?.pending ?? 0);

// TODO: Investigate nesting nav items (ie. pending composers, etc), but still must support
//         navigating to "root" item (default nested list treats "parent" as non-interactable).
const drawerCollaboratorNavItems = computed<DrawerNavItem[]>(() => [
  {
    exact: true,
    icon: mdiDashboard,
    to: { name: routeNames.collaborator.dashboard },
    title: "Dashboard",
  },
  {
    badge: submissionsForReview.value
      ? { color: "warning", label: "Pending", value: `+${submissionsForReview.value}` }
      : undefined,
    icon: mdiSubmissions,
    to: { name: routeNames.collaborator.submission.index },
    title: "Submissions",
  },
  {
    badge: pendingComposers.value
      ? { color: "warning", label: "Pending", value: `+${pendingComposers.value}` }
      : undefined,
    icon: mdiComposers,
    to: { name: routeNames.collaborator.composer.index },
    title: "Composers",
  },
  { icon: mdiTeam, to: { name: routeNames.collaborator.team }, title: "Team" },
  { icon: mdiSeasons, to: { name: routeNames.collaborator.season }, title: "Seasons" },
  {
    disabled: true,
    icon: mdiSettings,
    to: { name: routeNames.collaborator.settings },
    title: "Settings",
  },
]);

const { mobile } = useDisplay();
const account = useAuthAccount();
const route = useRoute();

const hideDrawer = computed(() => !account.isCollaborator || route.path.includes("logout"));
</script>

<template>
  <VNavigationDrawer
    v-if="!hideDrawer"
    :model-value="mobile ? showOnMobile : true"
    :permanent="!mobile"
    @update:model-value="emit('toggle-drawer')"
  >
    <VList color="primary" density="compact" nav>
      <VListItem
        v-for="navItem in drawerCollaboratorNavItems"
        :id="getElId('nav-item', navItem.title)"
        :key="navItem.title"
        :disabled="navItem.disabled"
        :exact="navItem.exact"
        :title="navItem.title"
        :to="navItem.to"
      >
        <template #append>
          <VBadge
            v-if="navItem.badge"
            v-tooltip="navItem.badge.label"
            :color="navItem.badge.color"
            :content="navItem.badge.value"
            inline
          />
        </template>
        <template #prepend>
          <VIcon :icon="navItem.icon" />
        </template>
      </VListItem>
    </VList>
  </VNavigationDrawer>
</template>
